// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// map.ts — the board's geometry and the two hand-written maps.
//
// ELEVEN BY THIRTEEN, AND THE NUMBER IS LOAD-BEARING. The whole colony is on screen at once: no
// camera, no pan, no pinch, no scroll. That deletes an entire class of bug (principle #20's usual
// root cause is something sized for the default that does not scale) and it is only affordable
// because the board is small. The size was chosen by arithmetic, not taste: at 375x635 — an iPhone
// with Safari's URL bar expanded, which is the common case and the one a 812-based budget misses —
// the height term of --cell gives (635 - 268) / 13 = 28.2px cells. A 13x17 board, which is what the
// first draft of this design wanted, computes to 22px in the same viewport.
//
// A 28px cell is smaller than the 44px hit-target floor, and that is legal here for exactly one
// reason, stated so nobody quietly breaks it: A TAP ON A TILE IS NEVER DESTRUCTIVE. It selects, it
// shows a cost field, or it deselects. Everything that commits — clear, cut, build, cancel — happens
// through a wheel sector or a chip that is itself >= 44px. Hit-target compliance is a property of the
// action surface, not of the tile.
//
// THE RIDGE IS THE GAME. Row 5 of the home map is a rock ridge with exactly three gaps (columns 2, 6
// and 9). Every journey between the camp in the south and the wood and forage in the north goes
// through one of them, which is what gives the snow something to sever. A map with no chokepoints is
// a map where the weather cannot do anything interesting, and "the tile with the highest traversal
// count" — the sentence the run summary exists to produce — would be meaningless.

/** Columns. */
export const COLS = 11;
/** Rows. */
export const ROWS = 13;
/** Tiles on the board. */
export const TILES = COLS * ROWS;

export const idx = (c: number, r: number): number => r * COLS + c;
export const colOf = (t: number): number => t % COLS;
export const rowOf = (t: number): number => (t / COLS) | 0;

/** Terrain. Built things (walls, doors, stores, workshops) are a separate layer in the state. */
export const enum Terrain {
  /** Walkable outdoor ground. Snow accumulates here. */
  Open = 0,
  /** A stand of timber. CUT harvests wood; in the home map it regrows, in Fastness it does not. */
  Stand = 1,
  /** A forage patch. Harvests raw food on the same terms. */
  Forage = 2,
  /** Natural rock. Impassable, and it is what makes the ridge a ridge. */
  Rock = 3,
}

/**
 * The two maps. Longwinter and Fastness share the home map — they differ on resource DENSITY,
 * roster and regrowth, not on geometry, which keeps the shared-map claim honest and halves the
 * surface the layout tests have to cover. The Crossing needs its own, because relocation is a
 * property of the terrain.
 *
 * Legend: `.` open · `T` stand · `f` forage · `#` rock
 */
const HOME: readonly string[] = [
  'TT..###.TTT',
  'T....#...TT',
  '..f.......T',
  '.ff.....T..',
  '.f......TT.',
  '##.###.##.#', // the ridge — gaps at columns 2, 6 and 9
  '...........',
  '...........',
  '...........',
  '...........',
  '...........',
  'T.........T',
  'TT.......TT',
];

/**
 * The Crossing. A ruined camp in the south, a sound longhouse shell in the north, and a FOUR-TILE
 * NECK at row 6 (columns 4..7). At dusk on day 4 everything south of the neck gains two permanent
 * snow depth and the southern firepit cannot be relit, so every single thing you want on day 5 has
 * to have come through that neck, four units to an armload, while the storm tries to plug it.
 */
const CROSSING: readonly string[] = [
  // The north keeps its own timber and forage down the two outer columns, clear of the longhouse
  // walls, so a colony that banks north before the fourth day has something to live ON afterwards.
  // The first version put the northern forage underneath the longhouse's own walls, which made the
  // whole mode unwinnable for every policy — a broken loop rather than difficulty.
  'T.........T',
  'T.f.......T',
  '..f.......T',
  '.T........T',
  '...........',
  // The neck: three rows of rock with the SAME four columns open through all of them. An earlier
  // version staggered the gaps and produced a sealed pocket — cols 4..7 of the middle row connected
  // to nothing at all, so the southern camp could not reach a single stand and every run died with
  // zero wood produced. The balance sim found it in its first pass, which is what it is for.
  '####....###',
  '####....###', // the neck — columns 4..7
  '####....###',
  '...........',
  '..T.....f..',
  '.TT.....ff.',
  'T.........T',
  'TT.......TT',
];

const CODE: Record<string, Terrain> = {
  '.': Terrain.Open,
  T: Terrain.Stand,
  f: Terrain.Forage,
  '#': Terrain.Rock,
};

function parse(rows: readonly string[], name: string): Uint8Array {
  if (rows.length !== ROWS) throw new Error(`${name}: ${rows.length} rows, expected ${ROWS}`);
  const out = new Uint8Array(TILES);
  for (let r = 0; r < ROWS; r++) {
    const line = rows[r];
    if (line.length !== COLS) throw new Error(`${name} row ${r}: ${line.length} columns, expected ${COLS}`);
    for (let c = 0; c < COLS; c++) {
      const t = CODE[line[c]];
      if (t === undefined) throw new Error(`${name} row ${r}: unknown terrain '${line[c]}'`);
      out[idx(c, r)] = t;
    }
  }
  return out;
}

/** Map ids. A mode names one of these; nothing else may. */
export const MAP_IDS = ['home', 'crossing'] as const;
export type MapId = (typeof MAP_IDS)[number];

const MAPS: Record<MapId, Uint8Array> = {
  home: parse(HOME, 'home'),
  crossing: parse(CROSSING, 'crossing'),
};

/** A fresh copy — callers mutate their own terrain (a felled stand becomes open ground). */
export function terrainOf(id: MapId): Uint8Array {
  return Uint8Array.from(MAPS[id]);
}

/** The raw ASCII, for tests that want to assert something about the map as written. */
export function asciiOf(id: MapId): readonly string[] {
  return id === 'home' ? HOME : CROSSING;
}

/**
 * The four-neighbourhood, written into `out` and returning the count. No allocation, because this is
 * called inside the Dijkstra inner loop, and it is the reason `neighbours` is not a generator.
 */
export function neighbours(t: number, out: Int32Array): number {
  const c = colOf(t);
  const r = rowOf(t);
  let n = 0;
  if (r > 0) out[n++] = t - COLS;
  if (r < ROWS - 1) out[n++] = t + COLS;
  if (c > 0) out[n++] = t - 1;
  if (c < COLS - 1) out[n++] = t + 1;
  return n;
}

/** Is this tile on the border? The border is always outdoors — see `isIndoors` in graph.ts. */
export function onBorder(t: number): boolean {
  const c = colOf(t);
  const r = rowOf(t);
  return c === 0 || r === 0 || c === COLS - 1 || r === ROWS - 1;
}

/** Manhattan distance, used only for tie-free presentation ordering — never for routing. */
export function manhattan(a: number, b: number): number {
  return Math.abs(colOf(a) - colOf(b)) + Math.abs(rowOf(a) - rowOf(b));
}

/**
 * The Crossing's neck row and the row the permanent drift falls south of. Named here rather than in
 * modes.ts because they are facts about the MAP, and a mode that moved them would be describing a
 * different map.
 */
export const CROSSING_NECK_ROW = 6;
export const CROSSING_ABANDON_ROW = 7;
